import _ from 'lodash';
import { AxiosInstance } from 'axios';
import pMemoize from 'p-memoize';
import ms from 'ms';
import { QwaSession, QwaUser } from '../../../auth/types';
import createHttpError from 'http-errors';
import { adminAxios } from '../../lookerAxios';
import {
  LOOKER_SCHEMA_SUFFIX_ATTR_ID,
  LOOKER_ROLE_IDS,
  USER_AUTH_TOKEN_VALIDITY,
  LOOKER_CONTENT_AI_USERS_GRP,
  CONTENT_AI_USER_TYPE,
} from '../../../constants/common';
import { TRIAL_SCHEMA } from '../../../config/auth';
import { isTestDriveUser } from '../authActions';

/**
 *
 * @param qwaUser
 * @param selected_schema
 * @param axiosInstance
 * @returns Updated qwaUser with a new looker user ID
 */
const createLookerUser = async (qwaSession: QwaSession, selected_schema: string, axiosInstance: AxiosInstance) => {
  const qwaUser = qwaSession.user;
  try {
    const lastIndex = qwaUser?.name?.lastIndexOf(' ') || 0;
    let params;
    if (lastIndex === -1) {
      params = { first_name: qwaUser.name };
    } else {
      params = {
        first_name: qwaUser?.name?.substring(0, lastIndex),
        last_name: qwaUser?.name?.substring(lastIndex),
      };
    }

    const user = await axiosInstance.post(`/users`, params);
    // looker group config for embed session
    const groups = await axiosInstance.get(`groups/search?filter_or=false&name=${selected_schema}`);
    // Search & add user into that tenants group
    let group_id;
    if (!groups?.data?.length) {
      console.log('Looker Group Not Found!');
      const group = await axiosInstance.post(`/groups`, { name: selected_schema });
      group_id = group.data.id;
    } else {
      const group_ids = groups.data.map((i) => i.id);
      group_id = group_ids[0];
    }
    if (group_id) {
      await axiosInstance.post(`/groups/${group_id}/users`, { user_id: user.data.id });
    }
    const lookerUserId = parseInt(user.data.id);
    qwaUser.looker_id = lookerUserId;
    await memoizedUpdateSchemaSuffix(qwaSession, selected_schema, axiosInstance);
    await memoizedAssignLookerUserRoles(qwaUser, axiosInstance);
    if (LOOKER_CONTENT_AI_USERS_GRP[process.env.NEXT_PUBLIC_APP_ENV] && qwaUser.userType === CONTENT_AI_USER_TYPE) {
      axiosInstance.post(`/groups/${LOOKER_CONTENT_AI_USERS_GRP[process.env.NEXT_PUBLIC_APP_ENV]}/users`, {
        user_id: user.data.id,
      });
    }
    params = { email: qwaUser.email };
    axiosInstance
      .post(`/users/${lookerUserId}/credentials_email`, params)
      .then((resp) => {
        console.log(
          'GetUserAuthToken : CreateLookerUser info',
          `User credentials updated at looker for ID ${lookerUserId}`
        );
      })
      .catch((err) => {
        console.log('GetUserAuthToken : createLookerUser credentials_email error', err.stack);
        console.log('GetUserAuthToken : createLookerUser credentials_email errors->desc', err.response.data.errors);
      });

    return qwaUser;
  } catch (error) {
    console.log('GetUserAuthToken : createLookerUser error', error);
    return qwaUser;
  }
};

const updateSchemaSuffix = async (session: QwaSession, selectedSchema: string, axiosInstance: AxiosInstance) => {
  const qwaUser = session.user;
  try {
    axiosInstance
      .patch(
        `/users/${qwaUser.looker_id}/attribute_values/${LOOKER_SCHEMA_SUFFIX_ATTR_ID[process.env.NEXT_PUBLIC_APP_ENV]}`,
        {
          value: selectedSchema,
        }
      )
      .then((resp) => {
        console.log(
          'GetUserAuthToken : updateSchemaSuffix info',
          `User schema_suffix for ${qwaUser.email} has been updated to ${selectedSchema}.`
        );
      })
      .catch((err) => {
        console.log('GetUserAuthToken : updateSchemaSuffix roles error', err);
      });
  } catch (error) {
    console.log('GetUserAuthToken : updateSchemaSuffix error', error);
    return false;
  }
};

const memoizedUpdateSchemaSuffix = pMemoize(updateSchemaSuffix, {
  // how often a user can change their schema
  maxAge: ms('5s'),
  cacheKey: (args) => {
    const qwaSession = args[0];
    // override default caching scheme to include both user + schema
    const qwaUser = qwaSession.user;
    const emailAddress = qwaUser.email;
    const currentSchema = qwaSession.selected_schema;
    const newSchema = args[1];

    return JSON.stringify({ emailAddress, currentSchema, newSchema });
  },
});

const assignLookerUserRoles = async (userDetails: QwaUser, axiosInstance?: AxiosInstance) => {
  try {
    axiosInstance = axiosInstance ? axiosInstance : await adminAxios();
    axiosInstance
      .put(`/users/${userDetails.looker_id}/roles`, LOOKER_ROLE_IDS[process.env.NEXT_PUBLIC_APP_ENV])
      .then((resp) => {
        console.log(
          'GetUserAuthToken : CreateLookerUser info',
          `User role at looker for ID ${userDetails.looker_id}  has been updated `
        );
      })
      .catch((err) => {
        console.log('GetUserAuthToken : createLookerUser roles error', err);
      });
  } catch (error) {
    console.log('GetUserAuthToken : assignLookerUserRoles error', error);
    return false;
  }
};

const memoizedAssignLookerUserRoles = pMemoize(assignLookerUserRoles, {
  cacheKey: JSON.stringify,
  // how often we try to adjust a user's roles
  maxAge: ms('5s'),
});

class LookerService {
  validateSession = (session: QwaSession) => {
    if (!session) {
      console.log('GetUserAuthToken : GetUserAuthToken error', 'Access Denied, missing authorization in cookies');
      const error = new createHttpError[401](`Access Denied`);
      throw error;
    }

    const qwaUser = session.user;
    const userEmail = qwaUser.email;
    const groups = qwaUser.groups;
    const hasGroups = groups.length > 0;

    if (!hasGroups) {
      console.log('GetUserAuthToken : GetUserAuthToken error', 'Access Denied, user is not assigned any groups');
      const error = new createHttpError[401](`Access Denied`);
      throw error;
    }

    // TODO this should not be possible, but seems to be happening
    if (!userEmail) {
      console.log(`GetUserAuthToken: GetUserAuthToken error`, 'Bad Request, no valid email for user');
      const error = new createHttpError[400](`Access Denied`);
      throw error;
    }

    return session;
  };

  findOrCreateLookerUserInfo = async (qwaSession: QwaSession) => {
    this.validateSession(qwaSession);
    try {
      let qwaUser = qwaSession.user;

      // const _isTestDriveUser = await isTestDriveUser(qwaSession);
      // workaround to fix test drive login for non Quattr users
      const userEmail = encodeURIComponent(qwaUser?.email || '');
      const groups = qwaUser.groups;
      const hasGroups = groups.length > 0;
      const selectedSchema = qwaSession.selected_schema;

      const default_schema = groups[0];
      const selected_schema = selectedSchema || default_schema;

      // check to see if selected_schema is valid
      if ((!selected_schema || !groups.includes(selected_schema)) && hasGroups) {
        const error = new Error(`Access Denied, unauthorized for schema`);
        console.log(`LookerService : findOrCreateLookerUserInfo error: ${error.message}`);
        throw error;
      }

      const axiosInstance = await adminAxios();
      const lookerUserInfo = await axiosInstance.get(`/users/search?email=${userEmail}`);
      const trialUserLookerId = parseInt(process.env.TRIAL_LOOKER_USER_ID || '-1');
      const isTrialUser = groups.length === 1 && groups[0] === TRIAL_SCHEMA;
      if (isTrialUser && trialUserLookerId !== -1) {
        qwaUser.looker_id = trialUserLookerId;
      } else {
        // if there is no looker user, we need to create one
        if (_.isEmpty(lookerUserInfo.data)) {
          if (hasGroups) {
            //if a okta group describing a looker role exists in id-token
            qwaUser = await createLookerUser(qwaSession, selected_schema, axiosInstance);
          } else {
            console.log(
              'LookerService : findOrCreateLookerUserInfo err',
              `While creating the user, no role defining group is found.`
            );
          }
        } else {
          if (Array.isArray(lookerUserInfo?.data) && lookerUserInfo.data.length > 0) {
            const looker_user_id = parseInt(lookerUserInfo?.data[0]?.id);
            if (looker_user_id) {
              const promises = lookerUserInfo.data.map(async (user) => {
                if (user.is_disabled) {
                  await axiosInstance.patch(`/users/${user.id}`, { is_disabled: false });
                }
              });
              await Promise.all(promises);
              qwaUser.looker_id = looker_user_id;
            }
          }
          if (hasGroups) {
            //if a okta group describing a looker role exists in id-token
            await memoizedUpdateSchemaSuffix(qwaSession, selected_schema, axiosInstance);
          } else {
            console.log(
              'LookerService : findOrCreateLookerUserInfo err',
              `While updating the user, no role defining group is found.`
            );
          }
        }
      }

      if (!qwaUser.looker_id) {
        console.error(`LookerService: findOrCreateLookerUserInfo`, `qwaUser.looker_id is null`, { qwaUser });
        const error = new createHttpError.BadRequest(`BadRequest - missing looker_user_id in session`);
        throw error;
      }

      const userTokenInfo = await axiosInstance.post(`/login/${qwaUser.looker_id}?associative=false`);
      const user_auth_token = userTokenInfo.data.access_token;
      // const userAuthToken = { user_auth_token: userTokenInfo.data.access_token };

      // update the session, in case there was a new looker user id
      qwaSession.user = qwaUser;
      qwaSession.user_auth_token = user_auth_token;
      qwaSession.user_auth_token_expiration = Date.now() + USER_AUTH_TOKEN_VALIDITY;

      return qwaSession;
    } catch (err) {
      // TODO we really should type-check err before casting
      const error = err as Error;
      console.error(`LookerService: findOrCreateLookerUserInfo error`, error);
      const httpError = new createHttpError.InternalServerError(
        `Unexpected error, check server logs for more information`
      );
      httpError.cause = error;
      throw httpError;
    }
  };
}

export default new LookerService();
